import { FC, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { ApiRequestConfigType } from 'requests/index.types';
import { useSelector } from 'react-redux';
import { userIdSelector } from 'store/selectors/userSelectors/userIdSelector';
import { userSelector } from 'store/selectors/userSelectors/userSelector';
import { API_GET_agencyCommunitiesV1 } from 'requests/GET_agencyCommunitiesV1';
import { API_GET_customerCommunitiesV2 } from 'constants/requestOptions';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { isUserObjectB2bType } from 'types/common.types';

export interface Community {
  id: string;
  name?: string;
}

export interface ReportsDataContextType {
  primaryUserId: string | null;
  allCommunities: Community[];
  allCommunityIds: string[];
  isLoadingCommunities: boolean;
}

const mapCommunity = (community: { id?: string | number; name?: string }): Community => {
  return {
    id: `${community?.id}`,
    name: community?.name,
  };
};

export const ReportsDataContext = createContext<ReportsDataContextType>(
  {} as ReportsDataContextType,
);

export const ReportsDataProvider: FC = ({ children }) => {
  const [allCommunities, setAllCommunities] = useState<Community[]>([]);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false);
  const { apiRequest } = useAPIActions();

  const currentUserId = useSelector(userIdSelector);
  const user = useSelector(userSelector);
  const isUserAgency = useSelector(isUserAgencySelector);

  const getCommunitiesRequestOpts = isUserAgency
    ? API_GET_agencyCommunitiesV1()
    : API_GET_customerCommunitiesV2();

  const fetchCommunitiesData = useCallback(async () => {
    setIsLoadingCommunities(true);
    await apiRequest(getCommunitiesRequestOpts as ApiRequestConfigType, {
      onSuccess: (response) => {
        if (isUserAgency) {
          setAllCommunities(response?.data.agency_communities.map(mapCommunity));
        } else {
          setAllCommunities(response?.data.communities.map(mapCommunity));
        }
        setIsLoadingCommunities(false);
      },
      onError: (error) => {
        console.error('Failed to fetch communities:', error);
        setIsLoadingCommunities(false);
      },
    });
  }, [apiRequest, getCommunitiesRequestOpts, isUserAgency]);

  useEffect(() => {
    if (!isLoadingCommunities && allCommunities.length === 0) {
      fetchCommunitiesData();
    }
  }, [fetchCommunitiesData, isLoadingCommunities, allCommunities.length]);

  const primaryUserId = useMemo(() => {
    if (user && isUserObjectB2bType(user) && user.type === 'SecondaryUser' && user.invited_by?.id) {
      return user.invited_by.id;
    }
    return currentUserId;
  }, [user, currentUserId]);

  const allCommunityIds = useMemo(() => {
    return allCommunities.map((community) => community.id);
  }, [allCommunities]);

  const contextValues: ReportsDataContextType = useMemo(() => {
    return {
      primaryUserId,
      allCommunities,
      allCommunityIds,
      isLoadingCommunities,
    };
  }, [primaryUserId, allCommunities, allCommunityIds, isLoadingCommunities]);

  return (
    <ReportsDataContext.Provider value={contextValues}>{children}</ReportsDataContext.Provider>
  );
};

export const useReportsDataContext = () => useContext<ReportsDataContextType>(ReportsDataContext);
